// DeleteEntityModal.tsx
import React from "react";
import { createPortal } from "react-dom";
import Loader from "@shared/components/Loader";

export interface DeleteEntityModalProps {
  isOpen: boolean;
  selectedEntity?: { name?: string; title?: string } | null;
  onClose: () => void;
  onConfirm: () => void;
  titleText?: string;
  detailsText?: string;
  cancelClassName?: string;
  confirmClassName?: string;
  confirmText?: string;
  isLoading?: boolean;
  confirmTestId?: string;
  customItemName?: string;
}

const DeleteEntityModal: React.FC<DeleteEntityModalProps> = ({
  isOpen,
  selectedEntity,
  onClose,
  onConfirm,
  titleText = "Are you sure you want to delete",
  detailsText = "This action will permanently delete this item(s) and any associated data. This cannot be undone.",
  cancelClassName = "",
  confirmClassName = "",
  confirmText = "Delete",
  isLoading = false,
  customItemName = "",
  confirmTestId = "delete-button",
}) => {
  if (!isOpen) return null;

  const content = (
    <div
      className="fixed inset-0 left-0 top-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-entity-title"
      aria-describedby="delete-entity-description"
    >
      <div
        className="bg-white rounded-lg shadow-xl py-4 px-2 mx-auto text-center max-w-lg w-full transform transition-transform duration-200 scale-100"
        onClick={(e) => e.stopPropagation()}
      >
        <img src="/thrash-bin.png" className="mb-3 mx-auto" alt="" />
        <h2
          id="delete-entity-title"
          className="text-base 2xl:text-lg font-semibold text-gray-800"
        >
          {titleText}
          <br />
          <span className="font-bold">
            {selectedEntity?.name ||
              selectedEntity?.title ||
              customItemName ||
              "this item"}
          </span>
          ?
        </h2>

        <p
          id="delete-entity-description"
          className="px-2 text-gray-600 mt-3 text-sm"
        >
          {detailsText}
        </p>

        <div className="px-4 mt-6">
          <div className="flex justify-between gap-2">
            <button
              onClick={onClose}
              type="button"
              className={`px-4 py-2 rounded-lg text-blue-600 border-blue-600 border-2 hover:bg-gray-100 transition w-full ${cancelClassName}`}
            >
              Cancel
            </button>
            <button
              data-testid={confirmTestId}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onConfirm();
              }}
              type="button"
              disabled={isLoading}
              className={`px-4 py-2 w-full rounded-lg text-white bg-red-500 hover:bg-red-600 transition disabled:bg-gray-400 ${confirmClassName}`}
            >
              {isLoading ? <Loader color="#fff" /> : confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default DeleteEntityModal;
